import { useCallback, useMemo } from 'react'
import { format } from 'date-fns'
import { useSetAtom } from 'jotai'
import { Ellipsis, Pause, Play, Trash2, Users } from 'lucide-react'
import { twMerge } from 'tailwind-merge'
import { Button, Menu, Popover, Tooltip } from '@highlight/ui'
import { useAudioNoteStatus } from '@renderer/hooks/audio/use-audio-note-selectors'
import { AUDIO_INDICATOR_COLOR, useAudioIndicatorTone } from '@renderer/hooks/audio/use-audio-indicator-tone'
import { useLiveRecordingControls } from '@renderer/hooks/audio/use-live-recording-controls'
import type { RecordingStatus } from '@shared/types/AudioNoteState'
import { AudioWaveform } from '@renderer/overlay/components/audio-waveform'
import { MeetingMicStatus } from '@renderer/overlay/components/meeting-mic-status'
import { ParticipantList } from '@renderer/overlay/components/participants'
import { meetingDeletePendingAtom } from '../atoms'
import type { MeetingViewModel } from '../use-meeting-view-model'

const STATUS_LABEL: Partial<Record<RecordingStatus, string>> = {
  starting: 'Starting…',
  paused: 'Paused',
  reconnecting: 'Reconnecting…',
  saving: 'Saving…',
}

interface MeetingViewLiveHeaderProps {
  model: MeetingViewModel
}

/**
 * Header for the meeting being recorded: title, recording status, and the
 * recording's own controls, which stand in for the floating bar's audio pill
 * on this view.
 */
export function MeetingViewLiveHeader({ model }: MeetingViewLiveHeaderProps) {
  const status = useAudioNoteStatus()
  const tone = useAudioIndicatorTone()
  const { isPaused, togglePause, endRecording } = useLiveRecordingControls('sidepanel meeting view')
  const isStarting = status === 'starting'
  const isSaving = status === 'saving'
  const setDiscardPending = useSetAtom(meetingDeletePendingAtom)
  const handleDiscard = useCallback(() => setDiscardPending(true), [setDiscardPending])

  const participants = useMemo(
    () => model.calendarEvent?.attendees.map((attendee) => attendee.email) ?? [],
    [model.calendarEvent],
  )

  return (
    <div data-tour-section="meeting-header" className="flex shrink-0 flex-col gap-3 px-6 py-4">
      <div className="flex items-start justify-between gap-2">
        <div className="flex min-w-0 flex-col gap-0.5">
          <h2 className="text-foreground type-ui-md truncate font-medium">{model.title}</h2>
          <div className="type-ui-xs flex items-center gap-1 font-medium">
            <span className={twMerge('flex items-center gap-1.5', AUDIO_INDICATOR_COLOR[tone])}>
              <AudioWaveform className="h-4" />
              {(status && STATUS_LABEL[status]) ?? 'Recording'}
            </span>
            {model.startedAt && (
              <span className="text-foreground-secondary">· started {format(model.startedAt, 'h:mmaaa')}</span>
            )}
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          <MeetingMicStatus variant="ghost" />
          {participants.length > 0 && <AttendeesButton participants={participants} />}
          <Menu>
            <Menu.Trigger variant="ghost" shape="square" aria-label="More">
              <Ellipsis aria-hidden="true" />
            </Menu.Trigger>
            <Menu.Content align="end">
              <Menu.Item variant="danger" onClick={handleDiscard}>
                <Trash2 aria-hidden="true" />
                Discard recording
              </Menu.Item>
            </Menu.Content>
          </Menu>
        </div>
      </div>
      <div className="flex gap-2">
        <Button
          variant="secondary"
          size="lg"
          block
          disabled={status === 'reconnecting' || isStarting || isSaving}
          onClick={togglePause}
          left={isPaused ? <Play aria-hidden="true" /> : <Pause aria-hidden="true" />}
        >
          {isPaused ? 'Resume' : 'Pause'}
        </Button>
        <Button
          variant="outline"
          tone="danger"
          size="lg"
          block
          disabled={isStarting}
          loading={isSaving}
          onClick={endRecording}
        >
          End
        </Button>
      </div>
    </div>
  )
}

function AttendeesButton({ participants }: { participants: Array<string> }) {
  return (
    <Popover>
      <Tooltip>
        <Tooltip.Trigger
          render={
            <Popover.Trigger
              render={
                <Button variant="ghost" shape="square" aria-label="Attendees">
                  <Users aria-hidden="true" />
                </Button>
              }
            />
          }
        />
        <Tooltip.Content>Attendees</Tooltip.Content>
      </Tooltip>
      <Popover.Content align="end" className="w-64">
        <ParticipantList participants={participants} />
      </Popover.Content>
    </Popover>
  )
}
